import React, { useEffect, useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";

/**
 * Real-time countdown ticker.
 */
export default function CountdownTimer({
    deadline,
    absolute = false,
    onExpire,
    testId,
    showIcon = true,
    className = "",
}) {
    // Compute the absolute target ONCE per deadline change to avoid interval churn on parent re-renders
    const target = useMemo(
        () => (absolute ? deadline : Date.now() + deadline),
        [deadline, absolute],
    );
    const [remaining, setRemaining] = useState(() => Math.max(0, target - Date.now()));
    const expiredRef = useRef(false);

    useEffect(() => {
        if (remaining <= 0) {
            if (!expiredRef.current) {
                expiredRef.current = true;
                onExpire?.();
            }
            return;
        }
        const iv = setInterval(() => {
            const r = Math.max(0, target - Date.now());
            setRemaining(r);
            if (r <= 0) {
                clearInterval(iv);
                if (!expiredRef.current) {
                    expiredRef.current = true;
                    onExpire?.();
                }
            }
        }, 1000);
        return () => clearInterval(iv);
    }, [target]);

    if (remaining <= 0) return null;

    const m = Math.floor(remaining / 60000);
    const s = Math.floor((remaining % 60000) / 1000);
    // Warn state when less than 3 minutes left
    const warn = remaining < 3 * 60 * 1000;

    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest whitespace-nowrap ${className}`}
            style={{ color: warn ? "#ff9d3f" : "#D4BC7C" }}
        >
            {showIcon && <Clock size={11} strokeWidth={2.2} />}
            {m > 0 ? `${m}min ${String(s).padStart(2, "0")}s` : `${s}s`}
        </span>
    );
}
